import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { ReactNode } from 'react';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { Category } from '@swf/core/types/StarsCoinReward';

import {
  TXTCLI_Free_Spins,
  TXTCLI_Instant_Bonuses,
  TXTCLI_Prize_Games,
  TXTCLI_Rewards_Store,
  TXTWCL_XfirstXMyX_firstXRewards
} from './CategorySelector.messages.json';
import styles from './CategorySelector.scss';

export interface Props {
  selected: Category | null | undefined;
  available: Category[];
  onSelect(category: Category): void;
}

const labels = {
  [Category.FSB]: TXTCLI_Free_Spins,
  [Category.Rewards]: TXTCLI_Rewards_Store,
  [Category.IB]: TXTCLI_Instant_Bonuses,
  [Category.SpinOfDay]: TXTCLI_Prize_Games
};

interface LabelProps {
  category: Category;
}

const Label = forwardRef<HTMLDivElement, LabelProps & Props>(
  ({ category, onSelect, selected }, ref) => {
    const translation = labels[category];
    const tabName = useTrackingLabel(translation);
    const { tabClicked } = useTrackers();

    return (
      <div
        key={category}
        className={classNames(styles.category, category === selected && styles.selected)}
        ref={ref}
        onClick={() => {
          if (tabName) tabClicked({ tabName });
          onSelect(category);
        }}
      >
        <div className={classNames(styles.icon, styles[category])} />

        <span className={styles.text}>{<Translation {...translation} />}</span>
      </div>
    );
  }
);

const lastClearThreshold = 5000;
const reminderCount = 3;
const visibilityThreshold = 0.5;

const CategorySelector: React.FC<Props> = ({ onSelect, selected, available }) => {
  const isMobile = useSelector(select.device.isMobile);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const disconnector = useRef<Function>(undefined);

  const localSettings = useSelector(select.localSettings.get);
  const {
    localSettings: { set }
  } = useDispatch<Dispatch>();

  const lastLogin = useSelector(select.account.getTimestamp) || 0;
  const needScrollMarker =
    lastLogin > (localSettings.lastLoginAcknowledged || 0) + lastClearThreshold;
  const [isTagVisible, setTagVisible] = useState<boolean>(false);

  const checkIntersection = useCallback(
    (records: IntersectionObserverEntry[]) => {
      if (records[0].isIntersecting && records[0].intersectionRatio >= visibilityThreshold) {
        setTagVisible(true);
        if (disconnector.current) {
          disconnector.current();
          set({ lastLoginAcknowledged: lastLogin });
        }
      }
    },
    [disconnector, lastLogin, set]
  );

  const tagRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const observer = new IntersectionObserver(checkIntersection, {
          threshold: visibilityThreshold,
          root: scrollerRef.current
        });
        observer.observe(node);
        disconnector.current = () => observer.disconnect();
      }
    },
    [checkIntersection]
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <Translation
          {...TXTWCL_XfirstXMyX_firstXRewards}
          values={{ first: (text: ReactNode) => <span className={styles.firstWord}>{text}</span> }}
        />
      </div>
      <div
        className={classNames(styles.selectorWrap, { [styles.mobile]: isMobile })}
        ref={scrollerRef}
      >
        {available.map((category, index) => (
          <Label
            available={available}
            key={category}
            ref={index === available.length - 1 && available.length > reminderCount ? tagRef : null}
            category={category}
            onSelect={onSelect}
            selected={selected}
          />
        ))}
      </div>
      {!isTagVisible && available.length > reminderCount && needScrollMarker && (
        <div className={classNames(styles.scrollReminder)} />
      )}
    </div>
  );
};

export default CategorySelector;
